import { TimeRangeValue } from '@components';
import { useWorkingHoursTimeOptions } from '@hooks';
import { useStore } from '@stores';
import { Form } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react';
import { useState } from 'react';
import Presentation from './UnavailabilityDrawer.presentation';

export type UnavailabilityMode = 'break' | 'fullDay' | 'range';

export interface UnavailabilityFormValues {
  name?: string;
  breakTime?: TimeRangeValue;
  dateRange?: [Dayjs, Dayjs];
}

interface IProps {
  name?: string;
  userId?: number;
  workStationId?: string;
  /** Day the break and full day options apply to. Defaults to today. */
  date?: Dayjs;
  setClose: () => void;
}

const atTime = (day: Dayjs, time: string) => {
  const [hour, minute] = time.split(':').map(Number);
  return day.hour(hour).minute(minute).second(0).millisecond(0);
};

export const UnavailabilityDrawer: React.FC<IProps> = observer(
  ({ name, userId, workStationId, date, setClose }) => {
    const { unavailabilityStore } = useStore();
    const [form] = Form.useForm<UnavailabilityFormValues>();
    const [mode, setMode] = useState<UnavailabilityMode>('break');

    const day = date ?? dayjs();
    const isPastDay = day.isBefore(dayjs(), 'day');
    const { options: timeOptions, hasWorkingHours, loading } = useWorkingHoursTimeOptions(day);

    const handleModeChange = (value: UnavailabilityMode) => {
      setMode(value);
      // the reason applies to every mode, only the mode specific inputs are cleared
      form.resetFields(['breakTime', 'dateRange']);
    };

    const buildPeriod = (values: UnavailabilityFormValues) => {
      switch (mode) {
        case 'fullDay':
          return {
            defaultName: 'Unavailable all day',
            startAt: day.startOf('day'),
            endAt: day.endOf('day'),
          };
        case 'break': {
          const { startTime, endTime } = values.breakTime!;
          return {
            defaultName: `Break ${startTime} - ${endTime}`,
            startAt: atTime(day, startTime!),
            endAt: atTime(day, endTime!),
          };
        }
        default: {
          const [start, end] = values.dateRange!;
          return { defaultName: 'Unavailable', startAt: start, endAt: end };
        }
      }
    };

    const handleSubmit = async (values: UnavailabilityFormValues) => {
      const period = buildPeriod(values);

      const created = await unavailabilityStore.create({
        userId,
        workStationId,
        name: values.name?.trim() || period.defaultName,
        startAt: period.startAt.format(),
        endAt: period.endAt.format(),
      });

      if (created) {
        form.resetFields();
      }
    };

    return (
      <Presentation
        form={form}
        name={name}
        userId={userId}
        workStationId={workStationId}
        day={day}
        isPastDay={isPastDay}
        mode={mode}
        timeOptions={timeOptions}
        hasWorkingHours={hasWorkingHours}
        workingHoursLoading={loading}
        setMode={handleModeChange}
        setClose={setClose}
        handleSubmit={handleSubmit}
      />
    );
  }
);
